import {
  isExpiredVPNWithNewSubscription,
  isNotActiveYet,
  isProcessingRenewal,
  isSafeSubscriptionClosed,
  isVPNSub,
} from "@/common/subscriptionHelper";
import { PAYPAL_CARD_TYPE, SafeDataPurgeDays } from "@/define";
import { ExpirationState, KeyCodeSeller, ZuoraSubscriptionStatus } from "@/globalEnums";
import { NotificationType, useNotificationsStore } from "@/stores/notifications";
import { useSubscriptionsStore } from "@/stores/subscriptions";
import { showAllstateBanner } from "../Allstate/AllstateHelper";
import { IAutoRenewRequest } from "@/common/api/unifiedPortal/interfaces";
import { useAutoRenewalStore } from "@/stores/autoRenewal";
import { usePayPalStore } from "@/stores/payPal";

export /**
 * Check all the subscriptions for subs that need a notification
 */
function processNotifications() {
  const notificationsStore = useNotificationsStore();
  const subscriptionsStore = useSubscriptionsStore();
  const autoRenewalStore = useAutoRenewalStore();
  const payPalStore = usePayPalStore();
  notificationsStore.clearNotifications();

  //Check if there was an error during the paypal flow
  if (payPalStore.purchaseError) {
    notificationsStore.addNotification({
      type: `${PAYPAL_CARD_TYPE}${payPalStore.purchaseError}` as NotificationType,
      dismissible: false,
    });

    //Clear the error from the store
    payPalStore.purchaseError = null;
    return;
  }

  if (subscriptionsStore.subscriptions.length === 0) {
    return;
  }
  //We only want to show the highest priority notifications
  // notification for editing payment from the manage payment column of the subscriptions table
  if (autoRenewalStore.isEditPayment) {
    notificationsStore.addNotification({
      type: "AddCreditCardSuccess",
    });
  }
  // notification for adding payment from the auto renew flow
  else if (
    sessionStorage.getItem("product_name_for_auto_renew") &&
    sessionStorage.getItem("subscription_autoRenewRequest")
  ) {
    const autoRenewRequest: IAutoRenewRequest = JSON.parse(sessionStorage["subscription_autoRenewRequest"]);
    const subscription = subscriptionsStore.subscriptions.find(
      s => s.zuoraSubscriptionNumber === autoRenewRequest.zuoraSubscriptionNumber
    );
    if (subscription?.isAutoRenewEnabled) {
      notificationsStore.addNotification({
        type: "AutoRenewTurnOnSuccess",
        // "product_name_for_auto_renew" is set to props.subscription.productDisplayName
        params: { carbonitePlan: sessionStorage.getItem("product_name_for_auto_renew") || undefined },
      });
      autoRenewalStore.isEditPayment = false;
      sessionStorage.removeItem("subscription_autoRenewRequest");
      sessionStorage.removeItem("product_name_for_auto_renew");
    }
  }

  // Allstate upgrade banner displays if More than 90 days from sub expiring and api returns showUpgradedBanner and flag is set to show allstate family
  showAllstateBanner();

  //Amazon Webroot, Only show banner for first 30 days of expiration
  if (
    subscriptionsStore.subscriptions.some(
      l => l.keyCodeSellerType === KeyCodeSeller.Amazon && l.expiration === ExpirationState.Expired
    )
  ) {
    notificationsStore.addNotification({ type: "SubscriptionsExpired", dismissible: false });
    return;
  }

  //Best Buy S2/TTS Webroot subs, Only show banner for first 30 days of expiration
  if (
    subscriptionsStore.subscriptions.some(
      l => l.keyCodeSellerType === KeyCodeSeller.BestBuy && l.expiration === ExpirationState.Expired
    )
  ) {
    notificationsStore.addNotification({ type: "SubscriptionsExpired", dismissible: false });
    return;
  }

  //outstanding Invoice
  if (subscriptionsStore.hasOutstandingInvoice) {
    notificationsStore.addNotification({ type: "SubscriptionsExpired", dismissible: false });
    return;
  }

  //Evergreen Monthly
  if (
    subscriptionsStore.subscriptions.some(
      l => l.isEvergreenMonthly && l.remainingDays <= 0 && !isSafeSubscriptionClosed(l)
    )
  ) {
    notificationsStore.addNotification({ type: "SubscriptionsExpired", dismissible: false });
    return;
  }

  //Direct Expired Banner
  if (
    subscriptionsStore.subscriptions.some(
      l =>
        (!l.resellerId || l.isPartnerReferral) &&
        l.zuoraSubscriptionStatus !== ZuoraSubscriptionStatus.Cancelled &&
        !l.isEvergreenMonthly &&
        !l.isTrial &&
        !isVPNSub(l) &&
        !isNotActiveYet(l) &&
        ((!l.isAutoRenewEnabled && l.expiration === ExpirationState.Expired) ||
          (l.isAutoRenewEnabled &&
            !isProcessingRenewal(l) &&
            l.remainingDays < 0 &&
            l.remainingDays > SafeDataPurgeDays.Reseller) ||
          (l.computer?.remainingDaysToPurge &&
            l.computer.remainingDaysToPurge > 0 &&
            !l.computer?.dataHasBeenPurged)) &&
        !l.is1xTo2xMigrationCatId
    )
  ) {
    notificationsStore.addNotification({ type: "SubscriptionsExpired", dismissible: false });
    return;
  }

  //Direct Trial Expired Banner
  if (
    subscriptionsStore.subscriptions.some(
      l =>
        (!l.resellerId || l.isPartnerReferral) &&
        l.isTrial &&
        l.remainingDays < 0 &&
        l.remainingDays > SafeDataPurgeDays.Trials
    )
  ) {
    notificationsStore.addNotification({ type: "SubscriptionsExpired", dismissible: false });
    return;
  }

  //Reseller Expired Banner at -29 days switch to direct message
  if (
    subscriptionsStore.subscriptions.some(
      l =>
        l.resellerId &&
        l.resellerId > 0 &&
        !l.isPartnerReferral &&
        !l.isTrial &&
        ((l.remainingDays <= -29 && l.remainingDays > SafeDataPurgeDays.Reseller) ||
          (l.computer?.remainingDaysToPurge && l.computer.remainingDaysToPurge > 0 && !l.computer?.dataHasBeenPurged))
    )
  ) {
    notificationsStore.addNotification({ type: "SubscriptionsExpired", dismissible: false });
    return;
  }

  //Reseller Expired Banner
  if (
    subscriptionsStore.subscriptions.some(
      l =>
        l.resellerId &&
        l.resellerId > 0 &&
        !l.isPartnerReferral &&
        !l.isTrial &&
        !l.is1xTo2xMigrationCatId &&
        ((!l.isAutoRenewEnabled && l.expiration === ExpirationState.Expired) ||
          (l.isAutoRenewEnabled &&
            !isProcessingRenewal(l) &&
            l.remainingDays < 0 &&
            l.remainingDays > SafeDataPurgeDays.Reseller) ||
          (l.computer?.remainingDaysToPurge && l.computer.remainingDaysToPurge > 0 && !l.computer?.dataHasBeenPurged))
    )
  ) {
    notificationsStore.addNotification({ type: "ResellerSubscriptionsExpired", dismissible: false });
    return;
  }

  //Webroot Expired
  if (
    subscriptionsStore.subscriptions.some(
      l =>
        l.keyCodeSellerType === KeyCodeSeller.Webroot &&
        !isNotActiveYet(l) &&
        (l.expiration === ExpirationState.Expired || l.expiration === ExpirationState.FinalWarning) &&
        !isVPNSub(l)
    )
  ) {
    notificationsStore.addNotification({ type: "SubscriptionsExpired", dismissible: false });
    return;
  }

  //VPN Expired
  if (
    subscriptionsStore.subscriptions.some(
      l =>
        isVPNSub(l) &&
        !isExpiredVPNWithNewSubscription(l, subscriptionsStore.subscriptions) &&
        (l.expiration === ExpirationState.Expired || l.expiration === ExpirationState.FinalWarning)
    )
  ) {
    notificationsStore.addNotification({ type: "SubscriptionsExpired", dismissible: false });
    return;
  }

  //Reseller Trial Expired Banner
  if (
    subscriptionsStore.subscriptions.some(
      l =>
        l.resellerId &&
        l.resellerId > 0 &&
        !l.isPartnerReferral &&
        l.isTrial &&
        l.remainingDays <= 0 &&
        l.remainingDays > SafeDataPurgeDays.Trials
    )
  ) {
    notificationsStore.addNotification({ type: "ResellerTrialSubscriptionsExpired", dismissible: false });
    return;
  }

  //Direct Expiring soon Banner
  if (
    subscriptionsStore.subscriptions.some(
      l =>
        l.keyCodeSellerType !== KeyCodeSeller.BestBuy &&
        (!l.resellerId || l.isPartnerReferral) &&
        !l.isAutoRenewEnabled &&
        !l.isEvergreenMonthly &&
        !l.isTrial &&
        l.expiration === ExpirationState.Upcoming &&
        !l.is1xTo2xMigrationCatId
    )
  ) {
    notificationsStore.addNotification({ type: "SubscriptionsAlmostExpired", dismissible: false });
    return;
  }

  //Reseller Expiring soon Banner
  if (
    subscriptionsStore.subscriptions.some(
      l =>
        l.resellerId &&
        l.resellerId > 0 &&
        !l.isPartnerReferral &&
        !l.isTrial &&
        l.expiration === ExpirationState.Upcoming &&
        !l.isAutoRenewEnabled
    )
  ) {
    notificationsStore.addNotification({ type: "ResellerSubscriptionsAlmostExpired", dismissible: false });
    return;
  }

  //Direct Trial Expiring soon Banner
  if (
    subscriptionsStore.subscriptions.some(
      l =>
        l.keyCodeSellerType !== KeyCodeSeller.BestBuy &&
        (!l.resellerId || l.isPartnerReferral) &&
        !l.isAutoRenewEnabled &&
        !l.isEvergreenMonthly &&
        l.isTrial &&
        l.expiration === ExpirationState.Upcoming
    )
  ) {
    notificationsStore.addNotification({ type: "SubscriptionWillExpireTrial", dismissible: false });
    return;
  }

  //Reseller Trial Expiring soon Banner
  if (
    subscriptionsStore.subscriptions.some(
      l =>
        l.resellerId &&
        l.resellerId > 0 &&
        !l.isPartnerReferral &&
        l.isTrial &&
        l.expiration === ExpirationState.Upcoming &&
        !l.isAutoRenewEnabled
    )
  ) {
    notificationsStore.addNotification({ type: "ResellerTrialSubscriptionsAlmostExpired", dismissible: false });
    return;
  }

  //Webroot expiring soon
  if (
    subscriptionsStore.subscriptions.some(
      l =>
        l.keyCodeSellerType === KeyCodeSeller.Webroot &&
        !isNotActiveYet(l) &&
        l.expiration === ExpirationState.Upcoming &&
        !l.isAutoRenewEnabled
    )
  ) {
    notificationsStore.addNotification({ type: "SubscriptionsAlmostExpired", dismissible: false });
    return;
  }

  //Best Buy Expiring soon Banner
  if (
    subscriptionsStore.subscriptions.some(
      l =>
        l.keyCodeSellerType === KeyCodeSeller.BestBuy &&
        !isNotActiveYet(l) &&
        l.expiration === ExpirationState.Upcoming &&
        !l.isAutoRenewEnabled
    )
  ) {
    notificationsStore.addNotification({ type: "SubscriptionsAlmostExpired", dismissible: false });
    return;
  }
}
